/* =================================================================
   DOPAMEAN — Search drawer
   One panel under the header search bar:
     • field empty  → suggested collections (server-rendered)
     • field typed  → matching products, fetched live from Shopify's
                       predictive-search JSON API and rendered as the
                       same collection-style rows.
   ================================================================= */
(function () {
  var DEBOUNCE_MS = 220;
  var PRODUCT_LIMIT = 6;

  function debounce(fn, wait) {
    var t;
    return function () {
      var ctx = this, args = arguments;
      window.clearTimeout(t);
      t = window.setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  // Word-by-word Title Case (e.g. "NIKE AIR MAX 90" -> "Nike Air Max 90")
  // -- same treatment as the collection title in header.liquid -- rather
  // than plain sentence case, which would flatten "Air"/"Max" to
  // lowercase on multi-word product titles.
  function titleCase(str) {
    return (str || '').toLowerCase().replace(/(^|\s)\S/g, function (c) {
      return c.toUpperCase();
    });
  }

  function init() {
    var form = document.querySelector('[data-dpm-search-form]');
    if (!form) return;

    var input = form.querySelector('#dpm-header-search-input');
    var panel = form.querySelector('[data-dpm-recent]');
    if (!input || !panel) return;

    var collectionsGroup = panel.querySelector('[data-dpm-collections]');
    var resultsGroup = panel.querySelector('[data-dpm-results]');
    var resultsList = panel.querySelector('[data-dpm-results-list]');
    var resultsEmpty = panel.querySelector('[data-dpm-results-empty]');
    var resultsViewAll = panel.querySelector('[data-dpm-results-viewall]');

    var isOpen = false;
    var controller = null;

    function open() {
      if (isOpen) return;
      panel.hidden = false;
      window.requestAnimationFrame(function () {
        panel.classList.add('is-open');
      });
      isOpen = true;
    }

    function close() {
      if (!isOpen) return;
      panel.classList.remove('is-open');
      isOpen = false;
      var onEnd = function () {
        // Guard against a reopen racing this transition's end (fast
        // blur->focus, common on mobile) — without this check a stale
        // listener from the previous close could hide the panel that
        // was just reopened, same guard the setTimeout fallback below
        // already has.
        if (!isOpen) panel.hidden = true;
        panel.removeEventListener('transitionend', onEnd);
      };
      panel.addEventListener('transitionend', onEnd);
      window.setTimeout(function () {
        if (!isOpen) panel.hidden = true;
      }, 260);
    }

    // Toggle which group shows based on whether the field has a query.
    // Use both the `hidden` attribute AND an explicit display so neither a
    // leftover attribute nor a stray CSS rule can keep the wrong group up.
    function showGroup(el) {
      if (!el) return;
      el.hidden = false;
      el.style.display = 'block';
    }
    function hideGroup(el) {
      if (!el) return;
      el.hidden = true;
      el.style.display = 'none';
    }
    function showCollections() {
      showGroup(collectionsGroup);
      hideGroup(resultsGroup);
    }
    function showResults() {
      hideGroup(collectionsGroup);
      showGroup(resultsGroup);
    }

    function renderProducts(products, term) {
      resultsList.innerHTML = '';

      if (!products.length) {
        if (resultsEmpty) resultsEmpty.style.display = '';
        return;
      }
      if (resultsEmpty) resultsEmpty.style.display = 'none';

      products.forEach(function (p) {
        var li = document.createElement('li');
        li.className = 'dpm-recent-search__item';

        var a = document.createElement('a');
        a.className = 'dpm-recent-search__link';
        a.href = p.url;

        var thumb = document.createElement('span');
        thumb.className = 'dpm-recent-search__thumb';
        thumb.setAttribute('aria-hidden', 'true');
        if (p.image) {
          var img = document.createElement('img');
          img.src = p.image;
          img.alt = '';
          img.width = 48;
          img.height = 48;
          img.loading = 'lazy';
          thumb.appendChild(img);
        } else {
          thumb.innerHTML =
            '<span class="dpm-recent-search__icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" focusable="false"><circle cx="10.5" cy="10.5" r="6.5"/><line x1="15.5" y1="15.5" x2="21" y2="21"/></svg></span>';
        }

        var label = document.createElement('span');
        label.className = 'dpm-recent-search__label';
        label.textContent = titleCase(p.title);

        var chev = document.createElement('span');
        chev.className = 'dpm-recent-search__chev';
        chev.setAttribute('aria-hidden', 'true');
        chev.innerHTML =
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" focusable="false"><polyline points="9 6 15 12 9 18"/></svg>';

        a.appendChild(thumb);
        a.appendChild(label);
        a.appendChild(chev);
        li.appendChild(a);
        resultsList.appendChild(li);
      });
    }

    function fetchProducts(term) {
      if (controller) controller.abort();
      controller = new AbortController();

      // Shopify's built-in predictive JSON endpoint — returns clean data
      // we render ourselves into the drawer's collection-style rows.
      var url = '/search/suggest.json' +
        '?q=' + encodeURIComponent(term) +
        '&resources[type]=product' +
        '&resources[limit]=' + PRODUCT_LIMIT +
        '&resources[options][unavailable_products]=last';

      fetch(url, {
        signal: controller.signal,
        headers: { 'Accept': 'application/json' }
      })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (data) {
          if (!data) return;
          var products = (((data.resources || {}).results || {}).products) || [];
          var mapped = products.map(function (p) {
            return {
              title: p.title,
              url: p.url,
              image: (p.featured_image && p.featured_image.url) || p.image || null
            };
          });
          renderProducts(mapped, term);
          if (resultsViewAll) {
            resultsViewAll.href = '/search?q=' + encodeURIComponent(term) + '&options%5Bprefix%5D=last';
          }
        })
        .catch(function (e) {
          if (e && e.name === 'AbortError') return;
        });
    }

    var onType = debounce(function () {
      var term = input.value.trim();
      if (!term) {
        showCollections();
        return;
      }
      showResults();
      fetchProducts(term);
    }, DEBOUNCE_MS);

    input.addEventListener('focus', function () {
      if (input.value.trim()) { showResults(); fetchProducts(input.value.trim()); }
      else showCollections();
      open();
    });
    input.addEventListener('click', open);
    input.addEventListener('input', function () {
      open();
      onType();
    });
    input.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') close();
    });
    input.addEventListener('blur', function () {
      window.setTimeout(function () {
        if (!form.contains(document.activeElement)) close();
      }, 120);
    });
    document.addEventListener('click', function (e) {
      if (!isOpen) return;
      if (!form.contains(e.target)) close();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
